import { useState } from 'react'
import {
  isSessionActive,
  listAttendanceRecords,
  listAttendanceSessions,
  signAttendance,
} from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function StudentAttendancePage() {
  const sessions = useAsyncData(listAttendanceSessions, [])
  const records = useAsyncData(listAttendanceRecords, [])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const signed = new Set((records.data || []).map((r) => r.session))

  async function onSign(sessionId: number) {
    setBusyId(sessionId)
    setMessage(null)
    try {
      await signAttendance(sessionId)
      setMessage('Attendance signed.')
      await records.reload()
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not sign')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-6 p-8">
      <h1 className="text-2xl font-bold text-slate-800">Attendance</h1>
      {message && <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}
      {sessions.loading && <p className="text-sm text-slate-500">Loading…</p>}
      {sessions.error && (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{sessions.error}</p>
      )}
      <div className="space-y-3">
        {(sessions.data || []).map((s) => {
          const active = isSessionActive(s)
          const already = signed.has(s.id)
          return (
            <div
              key={s.id}
              className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between"
            >
              <div>
                <p className="text-sm font-bold text-slate-800">
                  {s.course_title} ({s.course_code})
                </p>
                <p className="text-xs text-slate-500">
                  {s.session_date} · {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)} ·{' '}
                  {s.lecturer_name}
                </p>
                <p className="mt-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">
                  {active ? 'Active now' : already ? 'Signed' : 'Closed / upcoming'}
                </p>
              </div>
              <button
                type="button"
                disabled={!active || already || busyId === s.id}
                onClick={() => void onSign(s.id)}
                className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {already ? 'Signed' : busyId === s.id ? 'Signing…' : 'Sign present'}
              </button>
            </div>
          )
        })}
      </div>
      {!sessions.loading && (sessions.data || []).length === 0 && (
        <p className="text-sm text-slate-500">No attendance sessions for your courses.</p>
      )}
    </div>
  )
}
